import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDown, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { cn } from "@/utils/css";
import { TranslatedText } from "@/components/common/text";
import { fetchAllOffers, clearChangedCounts, clearOffersState, setIsOpen } from "@/state/offersSlice";
import { MyOfferCard, MyOfferEmpty } from "@/components/dataPages/loadboard";
import { OffersRefreshProvider } from "@/contexts/OffersRefreshContext";
import { useOfferStatusNotifications } from "@/hooks/useOfferStatusNotifications";
import { ClickOutside } from "@/components/common/other/ClickOutside";
import dayjs from "dayjs";

export const MyOffers = () => {
    const dispatch = useDispatch();
    const [localIsOpen, setLocalIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("pending");
    const { quotes, bookings, changedCounts } = useSelector((state) => state.offers);
    const intervalRef = useRef(null);

    // Show toast notifications for status changes
    useOfferStatusNotifications(quotes.data, bookings.data);

    const loadOffers = (isAutoRefresh = false) => {
        dispatch(fetchAllOffers({ isAutoRefresh }));
    };

    const resetInterval = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
            loadOffers(true);
        }, 60000);
    };

    useEffect(() => {
        // Initial fetch (not auto refresh)
        loadOffers(false);

        // Set up interval for auto refresh
        resetInterval();

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            dispatch(clearOffersState());
        };
    }, []);

    // Clear changed counts when switching tabs
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Clear count for the tab being viewed (if it has a count)
        if (tabId !== "history" && changedCounts[tabId] > 0) {
            dispatch(clearChangedCounts(tabId));
        }
    };

    // Clear changed counts for active tab when clicking on offers
    const handleOfferClick = () => {
        if (activeTab !== "history") {
            dispatch(clearChangedCounts(activeTab));
        }
    };

    const allOffers = useMemo(() => {
        return [
            ...(quotes.data || []).map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || []).map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const pendingOffers = useMemo(() => {
        return [
            ...(quotes.data || [])
                .filter((quote) => ["PENDING", "NOT_SUBMITTED"].includes(quote.status))
                .map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => ["PENDING", "NOT_SUBMITTED"].includes(booking.status))
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const counteredOffers = useMemo(() => {
        return (quotes.data || [])
            .filter((quote) => quote.status === "COUNTERED")
            .map((quote) => ({ ...quote, type: "quote" }))
            .sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data]);

    const acceptedOffers = useMemo(() => {
        return [
            ...(quotes.data || []).filter((quote) => ["ACCEPTED", "COUNTER_ACCEPTED"].includes(quote.status)).map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => booking.status === "ACCEPTED")
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const declinedOffers = useMemo(() => {
        return [
            ...(quotes.data || [])
                .filter((quote) => ["DECLINED", "CANCELLED", "EXPIRED", "COUNTER_REJECTED"].includes(quote.status))
                .map((quote) => ({ ...quote, type: "quote" })),
            ...(bookings.data || [])
                .filter((booking) => ["DECLINED", "CANCELLED", "EXPIRED"].includes(booking.status))
                .map((booking) => ({ ...booking, type: "booking" })),
        ].sort((a, b) => dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf());
    }, [quotes.data, bookings.data]);

    const tabs = [
        { id: "pending", label: "Pending", count: pendingOffers.length, changed: changedCounts.pending },
        { id: "accepted", label: "Accepted", count: acceptedOffers.length, changed: changedCounts.accepted },
        { id: "countered", label: "Countered", count: counteredOffers.length, changed: changedCounts.countered },
        { id: "declined", label: "Declined", count: declinedOffers.length, changed: changedCounts.declined },
        { id: "history", label: "History", count: allOffers.length },
    ];

    const getActiveOffers = () => {
        switch (activeTab) {
            case "pending":
                return pendingOffers;
            case "accepted":
                return acceptedOffers;
            case "countered":
                return counteredOffers;
            case "declined":
                return declinedOffers;
            case "history":
                return allOffers;
            default:
                return pendingOffers;
        }
    };

    const activeOffers = getActiveOffers();

    const totalNotifications = changedCounts.pending + changedCounts.accepted + changedCounts.declined + changedCounts.countered;
    const hasNotifications = totalNotifications > 0;

    const handleToggleOpen = (open) => {
        setLocalIsOpen(open);
        dispatch(setIsOpen(open));
    };

    const handleClickAway = () => {
        handleToggleOpen(false);
    };

    return (
        <OffersRefreshProvider value={resetInterval}>
            <ClickOutside onClick={handleClickAway}>
                <div className="relative">
                    <div
                        className={cn(
                            "h-[44px] flex items-center gap-[10px] transition-colors rounded-full px-4 border font-medium cursor-pointer",
                            {
                                // Has notifications: solid green button
                                "bg-cm-p-on-container-green text-cm-p-surface border-transparent": hasNotifications,
                                // Open without notifications: light green with green border
                                "bg-cm-p-container-green text-cm-p-text-primary border-cm-p-accent-100": localIsOpen && !hasNotifications,
                                // Closed without notifications: default gray
                                "bg-cm-p-surface text-cm-p-text-primary border-cm-p-outline": !localIsOpen && !hasNotifications,
                            },
                        )}
                        onClick={() => handleToggleOpen(!localIsOpen)}
                    >
                        <div className="flex items-center gap-2 text-[15px]">
                            <TranslatedText>My offers</TranslatedText>
                            {hasNotifications && (
                                <span className="bg-cm-p-container-green text-cm-p-on-container-green rounded-full text-sm font-medium size-6 text-center leading-6">
                                    {totalNotifications}
                                </span>
                            )}
                        </div>
                        {!hasNotifications && (
                            <ChevronDown
                                className={cn("w-4 h-4 transition ease-in shrink-0", {
                                    "rotate-180": localIsOpen,
                                    "text-cm-p-text-primary": localIsOpen,
                                    "text-cm-p-text-secondary": !localIsOpen,
                                })}
                            />
                        )}
                    </div>
                    <div
                        className={cn(
                            "absolute right-0 top-[50px] w-[600px] overflow-hidden transition-all duration-300 rounded-lg z-dropdown shadow-lg",
                            {
                                "max-h-[489px] border border-cm-p-outline": localIsOpen,
                                "max-h-0": !localIsOpen,
                            },
                        )}
                    >
                        <div className="h-[489px] bg-cm-p-surface flex flex-col">
                            <div className="flex items-center justify-between pl-4 pr-3 h-[44px]">
                                <span className="text-[14px] font-medium text-cm-p-text-primary">
                                    <TranslatedText>My offers</TranslatedText>
                                </span>
                                <button onClick={() => handleToggleOpen(false)} className="text-cm-p-text-primary">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex gap-1 px-1">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => handleTabChange(tab.id)}
                                        className={cn(
                                            "w-1/5 px-3 py-2 text-sm font-medium transition-colors bg-cm-p-surface-container border-[2px] border-cm-p-surface-container border-b-0 inline-flex items-center justify-start gap-1",
                                            {
                                                "bg-cm-p-surface": activeTab === tab.id,
                                            },
                                        )}
                                    >
                                        <TranslatedText>{tab.label}</TranslatedText>
                                        {tab.changed !== undefined && tab.changed > 0 && (
                                            <span className="bg-cm-p-on-container-green text-cm-p-container-green rounded-full px-1.5 py-0.5 text-xs font-medium min-w-[20px] text-center">
                                                {tab.changed}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>

                            <div className="flex-1 px-1 pt-1 overflow-y-auto">
                                {quotes.loading || bookings.loading ? (
                                    <div className="w-full h-full flex items-center justify-center text-cm-p-text-secondary">
                                        <TranslatedText>Loading...</TranslatedText>
                                    </div>
                                ) : activeOffers.length === 0 ? (
                                    <MyOfferEmpty tab={activeTab} />
                                ) : (
                                    <div className="w-full flex flex-col gap-1">
                                        {activeOffers.map((offer, index) => (
                                            <MyOfferCard offer={offer} index={index} key={offer.id} onInteract={handleOfferClick} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </ClickOutside>
        </OffersRefreshProvider>
    );
};
